/**
 * Install secret — per-installation HMAC key for save signing.
 *
 * # What this is
 *
 * Every installation (browser profile + origin) generates a unique 32-byte
 * random secret on first save. This secret is the HMAC key used to sign
 * save envelopes. It never leaves the device. It's the foundation of the
 * cross-install copy detection (Phase A integrity model): a save signed
 * with installation A's secret cannot be verified with installation B's
 * secret, so a copied save fails HMAC verification on the destination
 * device.
 *
 * # Why dedicated `meta` object store (Decision 4 of Phase B)
 *
 * Saves and install secret have different lifecycles. Saves rotate
 * (rolling 3 backups). Install secret is generated once and never
 * changes. Mixing them in one Dexie table couples their loading order:
 * we'd need to load saves to verify them, but we need the secret to
 * verify, but the secret would live in the same place as the saves...
 *
 * Separating them makes the boot sequence linear:
 *   1. Open Dexie database
 *   2. Read or create install secret from `meta`
 *   3. Read latest save from `saves`
 *   4. Verify save HMAC against secret
 *
 * # Why lazy generation (Decision 4 of Phase B)
 *
 * The secret is only meaningful at save time. An install that boots but
 * never saves doesn't need a secret. Lazy generation:
 *   - Avoids writing to IndexedDB on first boot (cleaner first-launch profile)
 *   - Folds secret-creation into the existing save error-handling path
 *   - No race with anything because nothing else needs the secret pre-save
 *
 * # Why this implementation pattern (sync getSecret over async IndexedDB)
 *
 * Phase A's `InstallSecretSource.getSecret()` interface is synchronous —
 * it returns `Uint8Array`, not `Promise<Uint8Array>`. IndexedDB is async.
 * The reconciliation: `IndexedDBInstallSecret` does an async `initialize()`
 * step (called by saveController during boot or first-save) that loads the
 * secret into memory. After initialize, getSecret() returns from the cached
 * memory copy synchronously.
 *
 * Synchronous getSecret is valuable because:
 *   - The save layer (`attachChecksum`, `verifyChecksum`) is synchronous.
 *     If getSecret were async, every save layer call would need to be
 *     refactored to async. Larger blast radius than a one-time async init.
 *   - The secret never changes after generation, so caching is correct.
 *
 * # Race safety
 *
 * If two browser tabs first-save simultaneously, both might race to
 * generate-and-write the secret. The `ensurePersisted` pattern below
 * uses a Dexie transaction with `'readwrite'` mode and a getOrCreate
 * semantic: each tab opens a transaction, reads existing secret, and
 * writes only if absent. Last writer wins; first reader after the race
 * sees the canonical value. Both tabs end up with byte-identical state
 * because IndexedDB transactions serialize.
 *
 * # The 32-byte choice
 *
 * HMAC-SHA256 produces 32-byte output. RFC 2104 recommends key length
 * equal to or greater than the hash output length. 32 random bytes from
 * `crypto.getRandomValues` provides ~256 bits of entropy — far above
 * any practical brute-force threshold.
 *
 * @see {@link "@aevons-wake/core/save/integrity"} — InstallSecretSource interface
 * @see {@link ./dexieAdapter.ts} — Dexie database with `meta` and `saves` stores
 */

import type { Dexie, Table } from 'dexie';
import type { InstallSecretSource } from '@aevons-wake/core';

/** The fixed key under which the install secret lives in the `meta` store. */
const INSTALL_SECRET_KEY = 'installSecret';

/** Length of the install secret in bytes. Tied to HMAC-SHA256 output length. */
const INSTALL_SECRET_LENGTH = 32;

/** Shape of a row in the `meta` Dexie table. */
export interface MetaRow {
  /** Primary key — string identifier (e.g., 'installSecret'). */
  key: string;
  /** Opaque bytes for that key. */
  value: Uint8Array;
}

/**
 * IndexedDB-backed install secret source.
 *
 * # Lifecycle
 *
 * 1. Construct with a Dexie instance (database must already be opened).
 * 2. Call `ensurePersisted()` to load existing secret or create new one.
 *    This is async because IndexedDB is async.
 * 3. After ensurePersisted resolves, `getSecret()` returns the secret
 *    synchronously from the cached in-memory copy.
 *
 * # Why not auto-initialize on construction
 *
 * Construction must be sync (Dexie tables exist immediately after
 * database opens). Initialization is async (IndexedDB read). Splitting
 * them keeps construction lightweight and lets the controller decide
 * when to pay the I/O cost (boot vs first-save).
 */
export class IndexedDBInstallSecret implements InstallSecretSource {
  private cached: Uint8Array | null = null;
  private readonly metaTable: Table<MetaRow, string>;

  constructor(metaTable: Table<MetaRow, string>) {
    this.metaTable = metaTable;
  }

  /**
   * Load the install secret from IndexedDB, generating it if absent.
   *
   * Idempotent — safe to call multiple times. After the first successful
   * call, subsequent calls return immediately from the in-memory cache.
   *
   * Race-safe — if two callers race, both see the same final secret.
   * The Dexie transaction serializes the read+write atomically.
   *
   * @throws {Error} if IndexedDB is unavailable or the underlying transaction fails
   */
  async ensurePersisted(): Promise<void> {
    if (this.cached !== null) {
      return; // Already initialized.
    }

    // Atomic read-or-create. The 'rw' transaction ensures that even if
    // two tabs race, exactly one secret value persists and both observe it.
    const secret = await this.metaTable.db.transaction(
      'rw',
      this.metaTable,
      async () => {
        const existing = await this.metaTable.get(INSTALL_SECRET_KEY);
        if (existing) {
          return existing.value;
        }
        const fresh = generateRandomBytes(INSTALL_SECRET_LENGTH);
        await this.metaTable.put({
          key: INSTALL_SECRET_KEY,
          value: fresh,
        });
        return fresh;
      },
    );

    this.cached = secret;
  }

  /**
   * Return the install secret synchronously.
   *
   * Must be called after `ensurePersisted()` has resolved at least once.
   * Returns the in-memory cached secret. Does NOT touch IndexedDB.
   *
   * @throws {Error} if called before ensurePersisted has resolved
   */
  getSecret(): Uint8Array {
    if (this.cached === null) {
      throw new Error(
        'IndexedDBInstallSecret.getSecret() called before ensurePersisted() resolved. ' +
          'This is a programming error — ensure the controller awaits ensurePersisted ' +
          'before any save layer call.',
      );
    }
    return this.cached;
  }

  /**
   * Test-only utility: clear the in-memory cache.
   *
   * Forces the next `getSecret()` call to throw and the next
   * `ensurePersisted()` call to re-read from IndexedDB. Used by tests
   * to verify load-from-disk paths after writes.
   *
   * Production code should never call this.
   */
  _resetCacheForTests(): void {
    this.cached = null;
  }
}

/**
 * Generate cryptographically-secure random bytes via the Web Crypto API.
 *
 * Uses `crypto.getRandomValues`, which is:
 *   - Available in every modern browser (and Node 18+, via globalThis.crypto)
 *   - Backed by the platform's CSPRNG (e.g., /dev/urandom on Linux,
 *     CryptGenRandom on Windows)
 *   - Synchronous — no async overhead
 *   - Cannot fail under normal conditions (only throws if the buffer is
 *     too large, which we don't trigger with 32 bytes)
 */
function generateRandomBytes(length: number): Uint8Array {
  const bytes = new Uint8Array(length);
  crypto.getRandomValues(bytes);
  return bytes;
}